/**
 * Shared typography/layout tokens for EntityUnifiedModal hosts.
 * Tab shell + FormShell width SSOT: `components.css`
 * (`.entity-modal-tab-content`, `.kiosk-unified-modal .entity-modal-form-shell`).
 * Section grids use `@container/entity-modal` on the tab content shell.
 */
/** Fills AppModal body height; single scroll lives on AppModal `.body` only. */
export const KIOSK_UNIFIED_MODAL_CLASS =
  'kiosk-unified-modal flex min-h-full w-full flex-1 flex-col';

/**
 * Tab body scrollport — hosts `@container/entity-modal` on tab content for section grids.
 */
export const KIOSK_UNIFIED_TAB_BODY_CLASS =
  'entity-modal-tab-body flex min-h-full flex-1 flex-col @container/entity-modal-viewport';

/** Card body container — inner grids key off card width, not modal shell (G4). */
export const ENTITY_MODAL_CARD_INNER_CONTAINER_CLASS = '@container/overview-card';

/** Inner 2-col grids inside section cards — card container, not modal shell (G4). */
export const ENTITY_MODAL_INNER_GRID_TWO_COL_CLASS =
  'grid gap-3 grid-cols-1 @min-[28rem]/overview-card:grid-cols-2';

/** @deprecated Modal-keyed inner grid — prefer {@link ENTITY_MODAL_INNER_GRID_TWO_COL_CLASS}. */
export const ENTITY_MODAL_INNER_GRID_TWO_COL_MODAL_CLASS =
  'grid gap-3 @min-[640px]/entity-modal:grid-cols-2';

/** Sensor / metric strip — scales with reading shell width (G4). */
export const ENTITY_MODAL_SENSOR_STRIP_GRID_CLASS =
  'm-0 grid list-none grid-cols-2 gap-2 p-0 @min-[640px]/entity-modal:grid-cols-3 @min-[960px]/entity-modal:grid-cols-5';

/** Promo / overview field pairs inside section cards. */
export const ENTITY_MODAL_FIELD_GRID_TWO_COL_CLASS =
  'grid grid-cols-1 gap-[var(--space-2)] @min-[640px]/entity-modal:grid-cols-2';

/** Form field pairs inside entity modal section cards. */
export const ENTITY_MODAL_FORM_FIELD_GRID_TWO_COL_CLASS =
  'grid grid-cols-1 gap-[var(--space-3)] @min-[640px]/entity-modal:grid-cols-2 @min-[640px]/entity-modal:gap-x-[var(--space-4)] @min-[640px]/entity-modal:gap-y-[var(--space-3)]';

/** Overview card grids: 2 columns when shell ≥640px. */
export const ENTITY_OVERVIEW_GRID_TWO_COL_CLASS = '@min-[640px]/entity-modal:grid-cols-2';

/**
 * Identity / hero media + facts row — **one** layout for Products (catalog) and Zásoby
 * (Inventory `scope=salesPoint` ProductUnifiedModal). Do not fork per surface.
 *
 * Narrow: single column, full track width.
 * Wide (≥28rem identity-hero): media ≤⅓ and facts ≤½ of the card/modal track — leftover stays empty.
 */
export const ENTITY_OVERVIEW_HERO_GRID_CLASS =
  '@container/identity-hero grid grid-cols-1 gap-4 justify-items-stretch @min-[28rem]/identity-hero:grid-cols-[minmax(0,33.333%)_minmax(0,50%)] @min-[28rem]/identity-hero:items-start @min-[28rem]/identity-hero:justify-start @min-[28rem]/identity-hero:gap-6';

/** CSS hook for jsdom layout shims — keep in sync with {@link ENTITY_OVERVIEW_HERO_GRID_CLASS}. */
export const ENTITY_OVERVIEW_HERO_GRID_HOOK_CLASS = 'entity-overview-hero-grid';

/** Overview identity card spans 2 section-grid columns when shell is wide (G6). */
export const ENTITY_MODAL_OVERVIEW_IDENTITY_SPAN_CLASS =
  'min-w-0 @min-[640px]/entity-modal:col-span-2';

/** Bottom overview row — full-width wrapper with 2-col pair for kiosks + quick-actions (G3). */
export const ENTITY_MODAL_OVERVIEW_BOTTOM_ROW_WRAPPER_CLASS =
  'entity-modal-overview-bottom-row min-w-0 @min-[640px]/entity-modal:col-span-full grid grid-cols-1 items-stretch gap-4 @min-[640px]/entity-modal:grid-cols-2';

/** Non-card grid cell — stretches with sibling section cards (error panels, etc.). */
export const ENTITY_MODAL_SECTIONS_GRID_STRETCH_CELL_CLASS =
  'entity-modal-sections-grid__stretch-cell flex min-h-full min-w-0 flex-col';

/** @deprecated Use {@link ENTITY_MODAL_OVERVIEW_BOTTOM_ROW_WRAPPER_CLASS} — cards inside wrapper only need min-w-0. */
export const ENTITY_MODAL_OVERVIEW_BOTTOM_ROW_CLASS = 'min-w-0';

/** Hero price emphasis — scales with shell width, not viewport. */
export const ENTITY_OVERVIEW_HERO_PRICE_CLASS =
  'text-[length:var(--font-size-h3)] @min-[640px]/entity-modal:text-[length:var(--font-size-h2)]';

/** Form grids inside entity modal — same 640px container breakpoint as overview. */
export const ENTITY_FORM_GRID_ONE_COL_OVERRIDE_CLASS =
  '@min-[640px]/entity-modal:grid-cols-1';

export const ENTITY_FORM_GRID_SPAN_TWO_CLASS = '@min-[640px]/entity-modal:col-span-2';

/**
 * Tab stacks with multiple panels (payments, pickup, overview sections).
 * Auto-fit columns from visible child count — breakpoints follow reading shell width (G4).
 */
export const ENTITY_MODAL_SECTIONS_GRID_CLASS =
  'entity-modal-sections-grid grid min-w-0 grid-cols-1 items-stretch gap-4 @min-[640px]/entity-modal:[grid-template-columns:repeat(auto-fit,minmax(min(100%,20rem),1fr))]';

/**
 * Fixed 1 → 2 → 4 section grid (product Historie: activity / ledger / meta / stats).
 * Container queries on `@container/entity-modal` — not viewport.
 */
export const ENTITY_MODAL_SECTIONS_GRID_FOUR_CLASS =
  'entity-modal-sections-grid grid min-w-0 grid-cols-1 items-stretch gap-4 @min-[640px]/entity-modal:grid-cols-2 @min-[960px]/entity-modal:grid-cols-4';

/**
 * Commerce channel cards — at most two channels; 2-col cap at shell width.
 */
export const ENTITY_MODAL_CHANNELS_GRID_CLASS =
  'entity-modal-sections-grid grid min-w-0 grid-cols-1 items-stretch gap-4 @min-[640px]/entity-modal:grid-cols-2';

/**
 * @deprecated Width cap — use universal shell in EntityUnifiedModal + `components.css`.
 * Kept for callers that only need `w-full` inside the shell (no nested max-width).
 */
export const ENTITY_MODAL_TAB_CONTENT_MAX_CLASS = 'w-full';

/**
 * Universal tab-body content shell — full modal width; field caps in `components.css`.
 * `@container/entity-modal` hosts section-grid breakpoints inside the shell.
 */
export const ENTITY_MODAL_TAB_CONTENT_SHELL_CLASS =
  'entity-modal-tab-content @container/entity-modal min-w-0 w-full';

/** headerMeta — same full-width track as tab content. */
export const ENTITY_MODAL_HEADER_META_CLASS =
  'entity-modal-header-meta min-w-0 w-full';

/** Shared panel primitive class — belt rules in `components.css` (G5). */
export const ENTITY_MODAL_PANEL_CLASS = 'entity-modal-panel';

/** Lifecycle tab outer panel — shared across entity unified modals (G8). */
export const ENTITY_UNIFIED_LIFECYCLE_PANEL_CLASS =
  `entity-unified-lifecycle-panel ${ENTITY_MODAL_PANEL_CLASS} rounded-xl border border-[var(--color-an-border)] bg-[var(--color-an-surface)] p-[var(--space-4)]`;

/** Stable class on every FormShell root — entity-modal full-track override in `components.css`. */
export const ENTITY_MODAL_FORM_SHELL_CLASS = 'entity-modal-form-shell';

/**
 * Entity modal FormShell policy — full tab track inside `.kiosk-unified-modal`.
 * Enforced via `.${ENTITY_MODAL_FORM_SHELL_CLASS}` in `components.css` (all testIds).
 * Interactive controls and form-section panels cap at `--container-form` (720px) via `components.css`.
 */
export const ENTITY_MODAL_FORM_SHELL_POLICY_NOTE =
  `components.css .kiosk-unified-modal .${ENTITY_MODAL_FORM_SHELL_CLASS}`;

/** Two-column form rows — width owned by universal shell + `.form-row` CSS. */
export const ENTITY_MODAL_FORM_ROW_MAX_CLASS = 'form-row w-full';
